/* eslint-disable no-use-before-define */
import { computed, getCurrentInstance, onBeforeUnmount, ref } from 'vue';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { v4 as uuidv4 } from 'uuid';

export function useCanvasDraggableVisibility() {
  const store = useAssortmentCanvasStore();
  const uid = getCurrentInstance()?.uid ?? uuidv4();
  const dragOwnerId = `canvas-draggable-${uid}`;

  const isVisible = ref(false);
  const productIdForPopup = ref(null);

  let fallbackAttached = false;
  let fallbackTimer = null;

  const isLocalDragOwner = computed(() => store.activeCanvasProductDragOwnerId === dragOwnerId);
  const isCanvasProductDragInProgress = computed(() => store.isCanvasProductDragInProgress);

  function clearTimers() {
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  }

  function detachFallbackListeners() {
    if (!fallbackAttached) return;
    window.removeEventListener('pointerup', onGlobalDragTerminator, true);
    window.removeEventListener('pointercancel', onGlobalDragTerminator, true);
    window.removeEventListener('dragend', onGlobalDragTerminator, true);
    fallbackAttached = false;
  }

  function attachFallbackListeners() {
    if (fallbackAttached) return;
    window.addEventListener('pointerup', onGlobalDragTerminator, true);
    window.addEventListener('pointercancel', onGlobalDragTerminator, true);
    window.addEventListener('dragend', onGlobalDragTerminator, true);
    fallbackAttached = true;
  }

  function endDrag() {
    clearTimers();
    detachFallbackListeners();
    if (isLocalDragOwner.value) {
      store.endCanvasProductDrag(dragOwnerId);
    }
  }

  function onGlobalDragTerminator() {
    if (!isLocalDragOwner.value) return;
    // sortable runs clean-up code on event end. The fallback timer ensures that we process
    // removing events after that happens. The timeout is 0 because it can run immediately after
    // but it needs that event processing to have fully finished first.
    if (fallbackTimer) clearTimeout(fallbackTimer);
    fallbackTimer = setTimeout(endDrag, 0);
  }

  // Wire these to vuedraggable's @start / @end
  function onDragStart() {
    store.beginCanvasProductDrag(dragOwnerId);
    attachFallbackListeners();
  }

  function onDragStop() {
    endDrag();
  }

  function visibilityChanged(visible) {
    isVisible.value = visible;
    if (!visible) productIdForPopup.value = null;
  }

  function delayPopup(productId) {
    productIdForPopup.value = productId;
  }

  // Keep content mounted while visible, while we own a drag, or while any
  // drag is in progress (so cross-spacebreak drops have live targets).
  const showProducts = computed(
    () => isVisible.value || isLocalDragOwner.value || isCanvasProductDragInProgress.value
  );

  const beingDragged = computed(() => isLocalDragOwner.value);

  onBeforeUnmount(endDrag);

  return {
    beingDragged,
    delayPopup,
    isCanvasProductDragInProgress,
    isVisible,
    onDragStart,
    onDragStop,
    productIdForPopup,
    showProducts,
    visibilityChanged,
  };
}
